import { Alert, Button, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { MetadataList } from '../../shared/detail/MetadataList';
import { reportDrilldownHref } from '../reports/ReportDrilldowns';

export function PartnerCreditHealthPanel({
  dataSource,
  partnerId,
}: {
  readonly dataSource: DataSource;
  readonly partnerId: string;
}) {
  const auth = useAuth();
  const { t } = useTranslation();
  const unavailable = t('reportSummaries.unavailable');
  const mock = dataSource === 'mock';
  const canViewReports =
    auth.status === 'authenticated' && auth.principal.permissions.includes('REPORT_VIEW');
  return (
    <Paper
      aria-label={t('partnerCredit.health.title')}
      component="section"
      sx={{ p: 2 }}
      variant="outlined"
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
        <Typography component="h2" variant="h6">
          {t('partnerCredit.health.title')}
        </Typography>
        <DataSourceBadge source={dataSource} />
      </Stack>
      <Alert severity={mock ? 'info' : 'warning'} sx={{ mb: 2 }}>
        {t(mock ? 'partnerCredit.health.mockNotice' : 'partnerCredit.health.unavailable')}
      </Alert>
      <MetadataList
        items={[
          { label: t('partnerCredit.health.status'), value: mock ? 'HEALTHY' : unavailable },
          { label: t('partnerCredit.health.successRate'), value: mock ? '99.98%' : unavailable },
          { label: t('partnerCredit.health.latency'), value: mock ? '184 ms' : unavailable },
          { label: t('partnerCredit.health.retries'), value: mock ? '2' : unavailable },
          { label: t('partnerCredit.health.unknownResults'), value: mock ? '0' : unavailable },
          { label: t('partnerCredit.health.oldestReservation'), value: mock ? '0 s' : unavailable },
          { label: t('partnerCredit.health.exceptions'), value: mock ? '0' : unavailable },
        ]}
      />
      {canViewReports ? (
        <Button
          component={Link}
          size="small"
          sx={{ mt: 1.5 }}
          to={reportDrilldownHref('partner-credit-reconciliation', { partnerId })}
        >
          {t('partnerCredit.health.exceptions')}
        </Button>
      ) : null}
    </Paper>
  );
}
